class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }


  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('active');
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

    // Page state is set BEFORE trapFocus. It used to run after, and when
    // trapFocus threw (its container can be missing after the drawer rebuild)
    // execution stopped here: `active` was already set on line 33 so the drawer
    // opened, but the background blur class was never reached. Verified 2026-07-25:
    // drawer open, body.alt-cart-open absent. Visual state must not depend on
    // focus management succeeding.
    document.body.classList.add('overflow-hidden');
    // Liquid-glass blur behind the drawer (same approved effect as the mega menu, design-system §4).
    document.body.classList.add('alt-cart-open');

    try {
      trapFocus(containerToTrapFocusOn, focusElement);
    } catch (e) {
      // Keyboard trapping is a progressive enhancement here; never let it break the open.
    }

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('alt-cart-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // ALT-CARTDRAWER-003 Phase D — shared add re-render. product-form.js's success
    // path calls this for BOTH the SSR upsell card AND the cart-recommendations.js
    // rebuilt card (both reuse <product-form>), so announcing + reclaiming focus here
    // covers every add without duplicating the add logic in product-form.js.
    this.announceItemAdded();
    this.focusAfterRender();

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.open();
  }

  // Announce the add to screen-reader users (WCAG 4.1.3). The message is localized in
  // cart-drawer.liquid (data-item-added-text on #CartDrawer-LiveRegionText, a role="status"
  // region) — no hardcoded English here. We fill the region and unhide it ~1s, mirroring
  // Dawn's updateLiveRegions timing. The region re-renders empty on each add, so writing
  // text is an empty→text mutation the live region announces.
  announceItemAdded() {
    const liveRegion = this.querySelector('#CartDrawer-LiveRegionText');
    if (!liveRegion) return;
    const message = liveRegion.getAttribute('data-item-added-text');
    if (!message) return;
    liveRegion.setAttribute('aria-hidden', 'false');
    liveRegion.textContent = message;
    setTimeout(() => {
      liveRegion.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  // Keep focus from being lost when the add destroys the pressed control (WCAG 2.4.3) —
  // e.g. the upsell/recommendation Add button removed by the re-render. Only fires while
  // the drawer is already open (the in-drawer add case); on a fresh open, Dawn's
  // open()/transitionend handler owns the initial focus. Anchor = the drawer heading:
  // it lives in .drawer__header, OUTSIDE the cart-drawer-items subtree that product-form's
  // parallel cartUpdate → cart.js onCartUpdate re-renders (replaceWith) a moment later, so
  // focus here survives that async second render — a line item inside cart-drawer-items
  // would not. Falls back to the first line item name only if the heading is missing.
  // trapFocus re-arms the focus trap on the freshly rendered DOM.
  focusAfterRender() {
    if (!this.classList.contains('active')) return;
    const anchor = this.querySelector('.drawer__heading') || this.querySelector('.cart-item__name');
    if (anchor) trapFocus(this, anchor);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
